// src/features/dashboard/profile/components/OffcanvasAddAmigos.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Drawer "Adicione seus amigos"
//   view "menu"  → dois botões: Buscar por nome | Link do perfil
//   view "search"→ campo de busca + lista de resultados
//   view "qr"   → QR code + botões de compartilhamento
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Offcanvas } from "react-bootstrap";
import QRCode from "qrcode";
import {
  IoArrowBack,
  IoCloseOutline,
  IoSearchOutline,
  IoQrCodeOutline,
  IoPersonAddOutline,
  IoShareSocialOutline,
  IoCopyOutline,
} from "react-icons/io5";
import PerfilNone from "../../../../../Assets/images/imagePerfilNull.svg";
import Style from "../../../../../styles/features/dashboard/OffcanvasAddAmigos.module.css";
import callFirebaseFunction from "../../../../../utils/firebaseFunctionCaller.js";
import { linkDoPerfil } from '../../../../social/linksSociais';


// ─── SearchView ───────────────────────────────────────────────────────────────

function SearchView({ onOpenProfile }) {
  const [query,   setQuery]   = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    try {
        const res = await callFirebaseFunction("searchUsersByName", { query: q.trim() });
        setResults(res?.users || []);
      setSearched(true);
    } catch (err) {
      console.error("searchUsersByName:", err);
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 500);
  }

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <div className={Style.searchView}>
      {/* Campo de busca */}
      <div className={Style.searchInputWrap}>
        <IoSearchOutline className={Style.searchInputIcon} size={18} />
        <input
          className={Style.searchInput}
          type="text"
          placeholder="Nome do usuário"
          value={query}
          onChange={handleChange}
          autoFocus
        />
        {loading && <div className={Style.searchSpinner} />}
      </div>

      {/* Resultados */}
      <div className={Style.resultsList}>
        {!searched && query.length < 2 && (
          <p className={Style.searchHint}>
            Digite ao menos 2 letras para buscar.
          </p>
        )}

        {searched && results.length === 0 && !loading && (
          <p className={Style.searchEmpty}>
            Nenhum usuário encontrado para "{query}".
          </p>
        )}

        {results.map((u) => (
          <button
            key={u.uid}
            className={Style.userCard}
            onClick={() => onOpenProfile(u.uid)}
          >
            <img
              src={u.photoUrl || PerfilNone}
              alt={u.name}
              className={Style.userCardAvatar}
              onError={(e) => { e.currentTarget.src = PerfilNone; }}
            />
            <span className={Style.userCardName}>{u.name}</span>
            <IoArrowBack size={14} className={Style.userCardArrow} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── QRView ──────────────────────────────────────────────────────────────────

function QRView({ uid, name }) {
  const [qrSrc,  setQrSrc]  = useState("");
  const [copied, setCopied] = useState(false);
  /* ERA `lesvita.com/u/{uid}`, que não casa com rota nenhuma: dois segmentos
     contra um curinga de um. O QR e o "compartilhar" do perfil apontavam para
     uma página inexistente. Agora sai do mesmo lugar que o resto do app. */
  const profileUrl          = linkDoPerfil(uid);

  useEffect(() => {
    QRCode.toDataURL(profileUrl, {
      width: 220, margin: 1, color: { dark: "#00095C", light: "#ffffff" },
    })
      .then(setQrSrc)
      .catch(console.error);
  }, [profileUrl]);

  async function handleShare() {
    try {
      await navigator.share({ url: profileUrl, title: `LesVita — ${name}` });
    } catch {
      handleCopy();
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Clipboard error:", err);
    }
  }

  return (
    <div className={Style.qrView}>
      <div className={Style.qrImageWrap}>
        {qrSrc
          ? <img src={qrSrc} alt="QR Code" className={Style.qrImage} />
          : <div className={Style.qrPlaceholder} />
        }
      </div>
      <p className={Style.qrName}>{name}</p>
      <p className={Style.qrUrl}>{profileUrl}</p>
      <div className={Style.qrActions}>
        <button className={Style.qrActionBtn} onClick={handleShare}>
          <IoShareSocialOutline size={18} />
          Enviar link
        </button>
        <button
          className={`${Style.qrActionBtn} ${copied ? Style.qrActionBtnCopied : ""}`}
          onClick={handleCopy}
        >
          <IoCopyOutline size={18} />
          {copied ? "Copiado!" : "Copiar link"}
        </button>
      </div>
    </div>
  );
}

// ─── OffcanvasAddAmigos ───────────────────────────────────────────────────────

const OffcanvasAddAmigos = ({ show, onClose, uid, name, onOpenProfile }) => {
  const [view, setView] = useState("menu"); // "menu" | "search" | "qr"

  // Reseta para menu toda vez que abre
  useEffect(() => {
    if (show) setView("menu");
  }, [show]);

  function handleBack() {
    setView("menu");
  }

  const showBack = view !== "menu";

  const titleMap = {
    menu:   "Adicione seus amigos",
    search: "Buscar por nome",
    qr:     "Link do seu perfil",
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      className={Style.offcanvas}
    >
      {/* ── Header ── */}
      <div className={Style.header}>
        <button className={Style.headerBack} onClick={showBack ? handleBack : onClose}>
          <IoArrowBack size={20} />
        </button>
        <span className={Style.headerTitle}>{titleMap[view]}</span>
        <button className={Style.headerClose} onClick={onClose}>
          <IoCloseOutline size={20} />
        </button>
      </div>

      <Offcanvas.Body className={Style.body}>
        {/* ── View: Menu inicial ── */}
        {view === "menu" && (
          <div className={Style.menuView}>
            <button
              className={Style.menuOptionBtn}
              onClick={() => setView("search")}
            >
              <div className={Style.menuOptionIcon}>
                <IoSearchOutline size={22} />
              </div>
              <div className={Style.menuOptionText}>
                <span className={Style.menuOptionLabel}>Buscar por nome</span>
                <span className={Style.menuOptionSub}>
                  Encontre usuários pelo primeiro nome
                </span>
              </div>
              <IoArrowBack size={16} className={Style.menuOptionArrow} />
            </button>

            <button
              className={Style.menuOptionBtn}
              onClick={() => setView("qr")}
            >
              <div className={Style.menuOptionIcon}>
                <IoQrCodeOutline size={22} />
              </div>
              <div className={Style.menuOptionText}>
                <span className={Style.menuOptionLabel}>Link do seu perfil</span>
                <span className={Style.menuOptionSub}>
                  Compartilhe seu QR code ou link
                </span>
              </div>
              <IoArrowBack size={16} className={Style.menuOptionArrow} />
            </button>
          </div>
        )}

        {/* ── View: Busca ── */}
        {view === "search" && (
          <SearchView
            onOpenProfile={(targetUid) => {
              onClose();
              onOpenProfile(targetUid);
            }}
          />
        )}

        {/* ── View: QR ── */}
        {view === "qr" && (
          <QRView uid={uid} name={name} />
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default OffcanvasAddAmigos;